import { Controller } from "@hotwired/stimulus";

/**
 * 複数ファイルアップロード
 * @example
 * <div
 *   data-controller="multiple-upload"
 *   data-multiple-upload-max-files-value="3">
 *   <template data-multiple-upload-target="inputTemplate">
 *     <input type="file" name="images[]" hidden>
 *   </template>
 *   <div data-multiple-upload-target="inputContainer">
 *     <!-- ここに <input> が追加される -->
 *   </div>
 *   <template data-multiple-upload-target="imageTemplate">
 *     <img class="foo bar">
 *     <button data-action="multiple-upload#remove" data-multiple-upload-index-param="">削除</button>
 *   </template>
 *   <div data-multiple-upload-target="imageListContainer">
 *     <!-- ここに画像が追加される -->
 *   </div>
 *   <button data-action="multiple-upload#add">追加</button>
 *  </div>
 */
export class MultipleUploadController extends Controller {
  static targets = ["inputTemplate", "inputContainer", "button", "imageTemplate", "imageListContainer"];
  static values = {
    // 最大ファイル数
    maxFiles: { type: Number, default: 3 },
    // ファイル数が最大に達した場合にボタンを無効にする
    disableButton: { type: Boolean, default: true },
  };

  #files = [];
  /** ファイルが選択されている<input> */
  #inputs = [];

  /**
   * <template data-multiple-upload-target="inputTemplate">
   *   <input type="file" name="images[]" hidden>
   * </template>
   * <div data-multiple-upload-target="inputContainer">
   *   <!-- ここに <input> が追加される -->
   * </div>
   * <button data-action="multiple-upload#add">追加</button>
   */
  add() {
    if (this.#files.length >= this.maxFilesValue) {
      return;
    }
    this.#clean();
    const container = this.inputContainerTarget;
    const content = this.inputTemplateTarget.content.cloneNode(true);
    const input = content.querySelector("input");
    input.onchange = this.#handleChange;
    container.appendChild(content);
    input.click();
  }

  /**
   * <template data-multiple-upload-target="imageTemplate">
   *   <img>
   *   <!-- index-paramは#render()で設定される -->
   *   <button
   *     data-action="multiple-upload#remove"
   *     data-multiple-upload-index-param="">削除</button>
   * </template>
   */
  remove(event) {
    const index = event.params.index;
    console.debug("remove", index, event.params, this.#inputs);
    this.#inputs[index].remove();
    this.#inputs.splice(index, 1);
    this.#update();
  }

  #handleChange = (event) => {
    const input = event.currentTarget;
    if (input.files.length > 0) {
      this.#inputs.push(input);
    }
    this.#update();
  };

  #clean() {
    // 画像が無いinputを消す
    for (const input of this.inputContainerTarget.querySelectorAll("input")) {
      if (input.files.length === 0) {
        input.remove();
      }
    }
  }

  #update() {
    this.#files = this.#inputs.flatMap((input) => [...input.files]);
    this.#render();
  }

  #render() {
    // 画像プレビュー
    if (this.hasImageListContainerTarget && this.imageTemplateTarget) {
      const container = this.imageListContainerTarget;
      container.replaceChildren(); // clear
      const template = this.imageTemplateTarget.content;
      this.#files.forEach((file, index) => {
        const content = template.cloneNode(true);
        // 画像URL
        const img = content.querySelector("img");
        img.src = URL.createObjectURL(file);
        // 削除ボタンにインデックスを設定
        const button = content.querySelector("[data-multiple-upload-index-param]");
        button.dataset.multipleUploadIndexParam = index;

        container.appendChild(content);
      });
    }
    // ボタンの無効化
    if (this.disableButtonValue && this.hasButtonTarget) {
      const disabled = this.#files.length >= this.maxFilesValue;
      this.buttonTarget.disabled = disabled;
      this.buttonTarget.ariaDisabled = disabled;
    }
  }
}
